import React, {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {ModeleReferentielTypePiece} from '../../../components/Models'
import {editReferentielTypePiece, getReferentielTypePiece} from '../../../components/requests'
import {LoadingData} from '../../../components/LoadingData'
import {useFormik} from 'formik'
import {TagInput} from '../../../components/inputs/TagInput'
import {shouldDisplayFonctionnalite} from '../../../components/helpers'
import {useAuth} from '../../modules/auth'
import {Form} from 'react-bootstrap'
import Select from "react-select";
import {useLocation} from "react-router";

interface LocationState {
  sites?: any
}

const ReferencementTypePieceEditPage = () => {
  const initialValues: ModeleReferentielTypePiece = {
    id: undefined,
    referencement: [],
    idRefTypePiece: undefined,
    idTypePiece: undefined,
    affichageKilometrage: false,
    venteReserveePro: false,
  }

  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const {referentielTypePieceId, typePieceId} = useParams()
  const location = useLocation()
  const state = location.state as LocationState | undefined

  const [data, setData] = useState<ModeleReferentielTypePiece>(initialValues)
  const [loading, setLoading] = useState(true)
  const [loadingSave, setLoadingSave] = useState(false)
  const [referencement, setReferencement] = useState<string[]>([])
  const [bulkInput, setBulkInput] = useState<string>('')
  const [sites, setSites] = useState<any>([])
  const [kilometrage, setKilometrage] = useState(false)
  const [venteRrvPro, setventeRrvPro] = useState(false)

  useEffect(() => {
    getReferentielTypePieceData()
  }, [referentielTypePieceId, typePieceId])

  const getReferentielTypePieceData = () => {
    getReferentielTypePiece(referentielTypePieceId, typePieceId)
      .then((response) => {
        if (response?.status === 200 && response?.data?.id !== undefined) {
          setData(response.data)
          setReferencement(response.data.referencement || [])
          setKilometrage(response?.data?.typePiece?.affichageKilometrage || false)
          setventeRrvPro(response?.data?.typePiece?.venteReserveePro || false)
          setSites(response?.data?.sites?.map((site) => ({
            value: site.id,
            label: site.nom,
          })) || [])
          setLoading(false)
        } else {
          navigate(`/referentiel-type-piece`)
        }
      })
      .catch(() => {
        setLoading(false)
        navigate(`/referentiel-type-piece`)
      })
  }

  const submitForm = async (
    fieldsToUpdate: Partial<ModeleReferentielTypePiece>,
    callback
  ): Promise<void> => {
    setLoadingSave(true)
    const newTags = bulkInput
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag !== '' && !referencement.includes(tag))

    const finalTags = [...referencement, ...newTags]
    const updatedSites = sites?.map((site) => ({
      id: site?.value ?? '',
      nom: site?.label ?? ''
    }))

    const updatedData = {
      ...data,
      affichageKilometrage: kilometrage,
      venteReserveePro: venteRrvPro,
      referencement: finalTags,
      dureeDisponibilite: fieldsToUpdate.dureeDisponibilite ?? 24,
      sites: updatedSites
    }

    let requestOk = false
    try {
      const clientRequest = await editReferentielTypePiece(
        referentielTypePieceId,
        typePieceId,
        updatedData
      )
      if (clientRequest?.status === 200) {
        requestOk = true
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setLoadingSave(false)
      callback()
      if (requestOk) {
        setData({...updatedData})
        navigate(`/referentiel-type-piece`)
      }
    }
  }

  const formik = useFormik({
    initialValues: {
      ...data,
      dureeDisponibilite: data.dureeDisponibilite ?? 24,
      sites: data.dureeDisponibilite ?? []
    },
    enableReinitialize: true,
    onSubmit: (values, {setSubmitting}) => {
      submitForm(values, () => setSubmitting(false))
    },
  })

  return (
    <>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>Modifier un type pièce</h3>
          </div>
        </div>
        <div>
          {loading ? (
            <LoadingData/>
          ) : (
            <form onSubmit={formik.handleSubmit} noValidate className='form'>
              <div className='card-body border-top p-9'>
                <div className='row'>
                  <div className='col-lg-10 col-sm-12'>
                    <div className='row mb-6'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>Nom type pièce</label>
                      <div className='col-lg-8 fv-row'>
                        <input
                          value={data?.typePiece?.nom ?? ''}
                          disabled
                          className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                        />
                      </div>
                    </div>
                    <div className='row mb-6'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>Numéro</label>
                      <div className='col-lg-8 fv-row'>
                        <input
                          value={
                            `${data?.typePiece?.sousCategorie?.categorie?.code ?? ''}` +
                            `${data?.typePiece?.sousCategorie?.code ?? ''}` +
                            `${data?.typePiece?.code ?? ''}`
                          }
                          disabled
                          className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                        />
                      </div>
                    </div>
                    {/* Champ durée de disponibilité (en heures) */}
                    <div className='row mb-6'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>
                        Durée de disponibilité (en heure)
                      </label>
                      <div className='col-lg-8 fv-row'>
                        <input
                          type='number'
                          min='1'
                          step='1'
                          name='dureeDisponibilite'
                          value={formik.values.dureeDisponibilite}
                          onChange={formik.handleChange}
                          onKeyDown={(e) => {
                            if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                              e.preventDefault()
                            }
                          }}
                          className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                        />
                      </div>
                    </div>
                    {/* Champ durée de disponibilité (en heures) */}
                    <div className='row mb-6'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>Référencement</label>
                      <div className='col-lg-8 fv-row'>
                        <TagInput
                          tags={referencement}
                          setTags={setReferencement}
                          bulkInput={bulkInput}
                          setBulkInput={setBulkInput}
                        />
                      </div>
                    </div>

                    {/* Debut Site */}
                    <div className='row mb-6'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>
                        Site
                      </label>
                      <div className='col-lg-8 fv-row'>
                        <Select
                          isClearable
                          isMulti
                          className='react-select-styled react-select-solid react-select-lg'
                          classNamePrefix='react-select'
                          options={state?.sites}
                          fieldName='sites'
                          value={sites}
                          onChange={(e) => setSites(e)}
                          placeholder='Sélectionner centres'
                          styles={{
                            control: (baseStyles) => ({
                              ...baseStyles,
                              height: '45px',
                            }),
                          }}
                        />
                      </div>
                    </div>
                    {/* Fin Site */}

                    {/*debut affichage kilometrage*/}
                    <div className='row mb-6'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>
                        Affichage du kilometrage
                      </label>
                      <div className='col-lg-8 fv-row'>
                        <div className='d-flex align-items-center mt-3'>
                          <Form.Check type='checkbox' className='mb-4 form-switch'>
                            <Form.Check.Input
                              className='me-2'
                              name={`affichageKilometrage`}
                              defaultChecked={data?.typePiece?.affichageKilometrage}
                              onClick={() => {
                                setKilometrage(!kilometrage)
                              }}
                            />
                          </Form.Check>
                        </div>
                      </div>
                    </div>
                    {/*fin affichage kilometrage*/}

                    {/*debut affichage vente reservee pro*/}
                    <div className='row mb-6'>
                      <label className='col-lg-4 col-form-label fw-bold fs-6'>
                        Vente reservée au professionnel
                      </label>
                      <div className='col-lg-8 fv-row'>
                        <div className='d-flex align-items-center mt-3'>
                          <Form.Check type='checkbox' className='mb-4 form-switch'>
                            <Form.Check.Input
                              className='me-2'
                              name={`venteReserveePro`}
                              defaultChecked={data?.typePiece?.venteReserveePro}
                              onClick={() => {
                                setventeRrvPro(!venteRrvPro)
                              }}
                            />
                          </Form.Check>
                        </div>
                      </div>
                    </div>
                    {/*fin affichage vente reservee pro*/}
                  </div>
                </div>
              </div>

              {shouldDisplayFonctionnalite(currentUser, 'MODIFIER REF TYPE PIECE') && (
                <div className='card-footer d-flex justify-content-end py-6 px-9'>
                  <button
                    type='button'
                    className='btn btn-light me-3'
                    disabled={loading}
                    onClick={() => {
                      formik.resetForm()
                      navigate(`/referentiel-type-piece`)
                    }}
                  >
                    Annuler
                  </button>

                  <button type='submit' className='btn btn-primary' disabled={loading || loadingSave}>
                    {!loadingSave ? (
                      'Enregistrer'
                    ) : (
                      <span className='indicator-progress' style={{display: 'block'}}>
                      Enregistrement...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                    )}
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  )
}

export {ReferencementTypePieceEditPage}
